import type { Goal } from '../types/timeline'

export type SaveNodeKind = 'goal' | 'task' | 'subtask'

export type SaveGuardResult = { allowed: true } | { allowed: false; message: string }

/**
 * Top-down save gate.
 *
 * Saving a goal must not implicitly save everything beneath it — the user
 * commits the root first, then children:
 *   - a goal saves freely,
 *   - a task saves only after its parent goal is saved,
 *   - a subtask saves only after its parent task is saved.
 *
 * `savedIds` holds the ids already committed in the current pending-save pass.
 * When blocked, `message` is the exact toaster copy to surface.
 */
export const canSaveNode = (
  goal: Goal,
  kind: SaveNodeKind,
  nodeId: string,
  savedIds: ReadonlySet<string>,
): SaveGuardResult => {
  if (kind === 'goal') return { allowed: true }

  if (kind === 'task') {
    return savedIds.has(goal.id)
      ? { allowed: true }
      : { allowed: false, message: 'You need to Save the goal first' }
  }

  const parentTask = goal.tasks.find((task) => task.subTasks.some((subTask) => subTask.id === nodeId))
  if (!parentTask) return { allowed: true }

  return savedIds.has(parentTask.id)
    ? { allowed: true }
    : { allowed: false, message: 'You need to Save the parent task first' }
}

/** Every node id in a goal tree — the full set that a complete save must cover. */
export const collectGoalNodeIds = (goal: Goal): string[] => [
  goal.id,
  ...goal.tasks.flatMap((task) => [task.id, ...task.subTasks.map((subTask) => subTask.id)]),
]

/** True once every node in the goal tree has been saved. */
export const isGoalFullySaved = (goal: Goal, savedIds: ReadonlySet<string>): boolean =>
  collectGoalNodeIds(goal).every((id) => savedIds.has(id))

/** Where the AI-pending save journey should focus next, after `justSavedId` was committed. */
export type PreorderPosition =
  | { kind: 'task'; taskId: string }
  | { kind: 'subtask'; taskId: string; subTaskId: string }
  | null

/**
 * Depth-first preorder traversal of the goal tree.
 *
 * After saving:
 *   - the goal → focus the first task (or null if the goal has no tasks).
 *   - a task   → focus that task's first subtask, or the next sibling task,
 *                or null if this was the last task with no subtasks.
 *   - a subtask → focus the next subtask in the same task, or the next task,
 *                 or null if this was the last leaf in the tree.
 *
 * Pure function — caller wires the result to selection state.
 */
export const nextPreorderNode = (
  goal: Goal,
  justSavedKind: SaveNodeKind,
  justSavedId: string,
): PreorderPosition => {
  if (justSavedKind === 'goal') {
    const first = goal.tasks[0]
    return first ? { kind: 'task', taskId: first.id } : null
  }

  if (justSavedKind === 'task') {
    const task = goal.tasks.find((candidate) => candidate.id === justSavedId)
    if (task && task.subTasks[0]) {
      return { kind: 'subtask', taskId: task.id, subTaskId: task.subTasks[0].id }
    }
    return advanceToNextTask(goal, justSavedId)
  }

  // subtask: find parent task, look for next sibling subtask, else next task.
  const parentTask = goal.tasks.find((task) =>
    task.subTasks.some((subTask) => subTask.id === justSavedId),
  )
  if (!parentTask) return null
  const index = parentTask.subTasks.findIndex((subTask) => subTask.id === justSavedId)
  const nextSibling = parentTask.subTasks[index + 1]
  if (nextSibling) {
    return { kind: 'subtask', taskId: parentTask.id, subTaskId: nextSibling.id }
  }
  return advanceToNextTask(goal, parentTask.id)
}

const advanceToNextTask = (goal: Goal, currentTaskId: string): PreorderPosition => {
  const index = goal.tasks.findIndex((task) => task.id === currentTaskId)
  const next = index >= 0 ? goal.tasks[index + 1] : undefined
  return next ? { kind: 'task', taskId: next.id } : null
}
